import React, { useCallback, useEffect, useRef, useState } from 'react'
import { PrimaryButton, SecondaryButton } from '../components/Button'
import {
  GOOGLE_RECAPTCHA_SITE_KEY,
  RADOM_COLORS,
  RADOM_TOKEN_EXPIRY_LOCAL_STORAGE_KEY,
  RADOM_TOKEN_LOCAL_STORAGE_KEY
} from '../util/Constants'
import { observer } from 'mobx-react'
import { InputLabel, TextInput } from './Input'
import Radom from '../state/Radom'
import Close from '../icons/Close'
import { useSearchParams } from 'react-router-dom'
import { errorToast, identifyVisitor, successToast } from '../util/Util'
import styled from 'styled-components'
import Checkmark from '../icons/AnimatedCheckmark'
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { LoginStep } from './WithAuthentication'

const LoginButton = styled(PrimaryButton)`
  background-color: ${RADOM_COLORS.NEW_BLUE} !important;
  border-radius: 25px;
  padding: 15px !important;
  font-size: 16px;
`

interface IProps {
  step: LoginStep
  setStep: (step: LoginStep) => void
}

type RecaptchaExecutor = (action: string) => Promise<string>

const EmailLoginInner = observer((props: IProps) => {
  const { executeRecaptcha } = useGoogleReCaptcha()
  const executeRecaptchaRef = useRef<RecaptchaExecutor | null>(
    executeRecaptcha ? async (action: string): Promise<string> => await executeRecaptcha(action) : null
  )
  const recaptchaScriptLoadPromiseRef = useRef<Promise<void> | null>(null)

  // const [step, setStep] = useState(props.step)
  const step = props.step

  const [err, setErr] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [code, setPin] = useState('')

  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    executeRecaptchaRef.current = executeRecaptcha
      ? async (action: string): Promise<string> => await executeRecaptcha(action)
      : null
  }, [executeRecaptcha])

  const getWindowRecaptchaExecutor = useCallback((): RecaptchaExecutor | null => {
    const windowGreCaptcha = (window as any)?.grecaptcha
    if (!windowGreCaptcha) {
      return null
    }

    if (
      windowGreCaptcha.enterprise &&
      typeof windowGreCaptcha.enterprise.ready === 'function' &&
      typeof windowGreCaptcha.enterprise.execute === 'function'
    ) {
      return async (action: string): Promise<string> => {
        return await new Promise((resolve, reject) => {
          windowGreCaptcha.enterprise.ready(async () => {
            try {
              const token = await windowGreCaptcha.enterprise.execute(GOOGLE_RECAPTCHA_SITE_KEY, { action })
              resolve(token)
            } catch (err) {
              reject(err)
            }
          })
        })
      }
    }

    if (
      typeof windowGreCaptcha.ready === 'function' &&
      typeof windowGreCaptcha.execute === 'function'
    ) {
      return async (action: string): Promise<string> => {
        return await new Promise((resolve, reject) => {
          windowGreCaptcha.ready(async () => {
            try {
              const token = await windowGreCaptcha.execute(GOOGLE_RECAPTCHA_SITE_KEY, { action })
              resolve(token)
            } catch (err) {
              reject(err)
            }
          })
        })
      }
    }

    return null
  }, [])

  const ensureRecaptchaScript = useCallback(async (): Promise<void> => {
    const existingExecutor = getWindowRecaptchaExecutor()
    if (existingExecutor) {
      return
    }

    if (!recaptchaScriptLoadPromiseRef.current) {
      const recaptchaHosts = ['https://www.google.com', 'https://www.recaptcha.net']

      const loadScript = async (id: string, src: string): Promise<void> => {
        await new Promise<void>((resolve, reject) => {
          const existingScript = document.getElementById(id) as HTMLScriptElement | null
          if (existingScript) {
            if (getWindowRecaptchaExecutor()) {
              resolve()
              return
            }

            existingScript.remove()
          }

          const script = document.createElement('script')
          script.id = id
          script.src = src
          script.async = true
          script.defer = true
          script.onload = () => resolve()
          script.onerror = () => reject(new Error(`Failed to load script: ${src}`))

          document.head.appendChild(script)
        })
      }

      const loadScriptFromHosts = async (
        idPrefix: string,
        pathWithQuery: string
      ): Promise<void> => {
        let lastError: Error | null = null

        for (const host of recaptchaHosts) {
          const hostSuffix = host.includes('google.com') ? 'google' : 'recaptcha-net'
          const id = `${idPrefix}-${hostSuffix}`
          const src = `${host}${pathWithQuery}`

          try {
            await loadScript(id, src)
            if (getWindowRecaptchaExecutor()) {
              return
            }
          } catch (err) {
            if (err instanceof Error) {
              lastError = err
            } else {
              lastError = new Error(String(err))
            }
          }
        }

        if (lastError) {
          throw lastError
        }

        throw new Error(`Failed to load reCAPTCHA script: ${pathWithQuery}`)
      }

      recaptchaScriptLoadPromiseRef.current = (async () => {
        const encodedSiteKey = encodeURIComponent(GOOGLE_RECAPTCHA_SITE_KEY)
        await loadScriptFromHosts(
          'radom-recaptcha-fallback-script',
          `/recaptcha/api.js?render=${encodedSiteKey}`
        )

        if (getWindowRecaptchaExecutor()) {
          return
        }

        await loadScriptFromHosts(
          'radom-recaptcha-fallback-enterprise-script',
          `/recaptcha/enterprise.js?render=${encodedSiteKey}`
        )
      })()
        .catch(err => {
          recaptchaScriptLoadPromiseRef.current = null
          throw err
        })
    }

    await recaptchaScriptLoadPromiseRef.current
  }, [getWindowRecaptchaExecutor])

  const getErrorMessage = useCallback((error: unknown): string => {
    if (typeof error === 'object' && error !== null) {
      const maybeAxios = error as {
        response?: { data?: { error?: string } }
        reason?: string
        message?: string
      }

      return maybeAxios.response?.data?.error || maybeAxios.reason || maybeAxios.message || 'Something went wrong'
    }

    if (typeof error === 'string') {
      return error
    }

    return 'Something went wrong'
  }, [])

  const waitForRecaptcha = useCallback(async (): Promise<RecaptchaExecutor> => {
    const timeoutMs = 10000
    const pollMs = 100
    const start = Date.now()
    let triedFallbackScriptLoad = false

    while (Date.now() - start < timeoutMs) {
      const execute = executeRecaptchaRef.current
      if (execute) {
        return execute
      }

      const windowExecutor = getWindowRecaptchaExecutor()
      if (windowExecutor) {
        return windowExecutor
      }

      if (!triedFallbackScriptLoad) {
        triedFallbackScriptLoad = true
        try {
          await ensureRecaptchaScript()
        } catch (err) {
          console.error('Failed to bootstrap reCAPTCHA script:', err)
        }
      }

      await new Promise(resolve => setTimeout(resolve, pollMs))
    }

    throw new Error('Security check failed to initialize. Please disable blockers and refresh.')
  }, [ensureRecaptchaScript, getWindowRecaptchaExecutor])

  const getRecaptchaToken = useCallback(async (action: string): Promise<string> => {
    try {
      const execute = executeRecaptchaRef.current || await waitForRecaptcha()
      const token = await execute(action)
      if (!token) {
        throw new Error('Security check failed. Please refresh and try again.')
      }

      return token
    } catch (err) {
      const message = getErrorMessage(err)
      errorToast(message)
      throw new Error(message)
    }
  }, [getErrorMessage, waitForRecaptcha])

  useEffect(() => {
    const action = searchParams.get('action')
    const urlEmail = searchParams.get('email')
    const urlPin = searchParams.get('code')
    if (urlEmail && urlPin) {
      setEmail(urlEmail)
      setPin(urlPin)
      if (action === 'reset_password') {
        props.setStep(LoginStep.PasswordResetCodeVerified)
      } else {
        props.setStep(LoginStep.CompleteSignUp)
      }
    }
  }, [searchParams])

  useEffect(() => {
    setErr('')
  }, [step])

  const completeSignUp = async (): Promise<void> => {
    if (password !== passwordConfirm) {
      return setErr('Passwords do not match')
    }

    setErr('')
    setIsLoading(true)

    Radom.completeEmailSignUp(email, password, code)
      .then(res => {
        Radom.token = res.jwtToken
        try {
          localStorage.setItem(RADOM_TOKEN_LOCAL_STORAGE_KEY, Radom.token)
          localStorage.setItem(RADOM_TOKEN_EXPIRY_LOCAL_STORAGE_KEY, new Date(res.expiresAt).toString())
        } catch (err) {
          localStorage.clear()
          window.location = '/' as any
        }
        setSearchParams({})

        Radom.isLoggedIn = !res.requires2fa
        Radom.requires2fa = res.requires2fa
        if (!Radom.requires2fa) {
          successToast('Successfully signed in!')
        }
      })
      .catch(err => {
        setErr(getErrorMessage(err))
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const login = async (): Promise<void> => {
    setErr('')
    setIsLoading(true)

    try {
      const recaptchaToken = await getRecaptchaToken('email_login')
      const res = await Radom.emailSignIn(email, password, recaptchaToken)
      Radom.token = res.jwtToken
      try {
        localStorage.setItem(RADOM_TOKEN_LOCAL_STORAGE_KEY, Radom.token)
        localStorage.setItem(RADOM_TOKEN_EXPIRY_LOCAL_STORAGE_KEY, new Date(res.expiresAt).toString())
      } catch (err) {
        localStorage.clear()
        window.location = '/' as any
      }
      setSearchParams({})

      Radom.isLoggedIn = !res.requires2fa
      Radom.requires2fa = res.requires2fa
      if (!res.requires2fa) {
        successToast('Successfully signed in!')
      }

      try {
        const visitorTokenResponse = await Radom.generateVisitorToken()
        localStorage.setItem('visitorToken', visitorTokenResponse.token)
        await identifyVisitor()
      } catch (visitorErr) {
        console.error('Failed to generate visitor token or identify visitor:', visitorErr)
      }
    } catch (err) {
      setErr(getErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  const header = (step: LoginStep): string => {
    switch (step) {
      case LoginStep.Login:
      case LoginStep.StartSignUp:
      case LoginStep.CompleteSignUp:
        return ''
      case LoginStep.VerifySignUp:
        return 'Verification email sent'
      case LoginStep.ForgotPassword:
      case LoginStep.PasswordResetEmailSent:
      case LoginStep.PasswordResetCodeVerified:
        return 'Reset password'
    }
  }

  const subHeader = (step: LoginStep): string => {
    switch (step) {
      case LoginStep.Login:
      case LoginStep.StartSignUp:
      case LoginStep.CompleteSignUp:
        return ''
      case LoginStep.VerifySignUp:
        return 'Check your email for instructions to complete your sign up'
      case LoginStep.ForgotPassword:
        return 'We’ll send an email with reset instructions'
      case LoginStep.PasswordResetEmailSent:
        return 'Check your email for instructions to reset your password'
      case LoginStep.PasswordResetCodeVerified:
        return 'Enter your new password'
    }
  }

  return <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 15,
      width: '100%',
      fontSize: 14
    }}>

      {
        header(step) &&
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 10 }}>

          {
            (step === LoginStep.VerifySignUp || step === LoginStep.PasswordResetEmailSent) &&
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: RADOM_COLORS.SUCCESS,
                  width: 35,
                  height: 35,
                  borderRadius: '100%'
                }}>

                  <Checkmark
                    animationSpeed={'1s'}
                    animationDelay={'0s'}
                    color='white'
                  />
                </div>

              </div>

            </>
          }
          <span style={{ fontSize: 24, fontWeight: 300, letterSpacing: -1 }}>{header(step)}</span>
          <span style={{ fontSize: 14, color: RADOM_COLORS.GRAY_DARKEST }}>{subHeader(step)}</span>
        </div>
      }

      {
        err &&
        <div style={{
          backgroundColor: RADOM_COLORS.ERROR,
          borderRadius: 5,
          padding: '10px 12px',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 10
        }}>
          <div style={{
            backgroundColor: 'white',
            width: 18,
            height: 18,
            borderRadius: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Close style={{ width: 8 }} stroke={RADOM_COLORS.ERROR} strokeWidth={2} />
          </div>
          <span>{err}</span>
        </div>
      }

      {
        step === LoginStep.Login &&
        <form
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 15,
            width: '100%',
            fontSize: 14
          }}
          onSubmit={e => {
            e.preventDefault()
            login()
          }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <InputLabel style={{ color: RADOM_COLORS.NEW_GRAY_1, opacity: 1 }}>Email address</InputLabel>
            <TextInput
              placeholder='Enter email address'
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={isLoading}
              autoFocus />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <InputLabel style={{ color: RADOM_COLORS.NEW_GRAY_1, opacity: 1 }}>
                <span>Password</span>
              </InputLabel>
              <TextInput
                type='password'
                placeholder='Enter password'
                value={password}
                disabled={isLoading}
                onChange={e => setPassword(e.target.value)} />
            </div>

            <div style={{ display: 'flex', gap: 5, fontSize: 12 }}>
              <span>Forgot your password?</span>
              <SecondaryButton
                type='button'
                style={{
                  padding: 0,
                  border: 0,
                  display: 'inline-block',
                  width: 'fit-content',
                  color: RADOM_COLORS.NEW_GRAY_1,
                  fontSize: 12,
                  fontWeight: 500,
                  textDecoration: 'underline'
                }}
                disabled={isLoading}
                onClick={() => props.setStep(LoginStep.ForgotPassword)}>
                Reset it
              </SecondaryButton>
            </div>
          </div>

          <LoginButton
            isLoading={isLoading}
            style={{
              backgroundColor: RADOM_COLORS.ORANGE,
              padding: 15
            }}
            onClick={async () => await login()}>
            <span>Continue</span>
          </LoginButton>

          {/* <div style={{ display: 'flex', gap: 5, alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: 14 }}>Don’t have an account?</span>
          <SecondaryButton
            type='button'
            disabled={isLoading}
            style={{
              padding: 0,
              border: 0,
              color: RADOM_COLORS.NEW_GRAY_1,
              textDecoration: 'underline',
              fontWeight: 500
            }}
            onClick={() => props.setStep(LoginStep.StartSignUp)}>
              Sign up
          </SecondaryButton>
        </div> */}
        </form>
      }

      {
        step === LoginStep.StartSignUp &&
        <form
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 15,
            width: '100%',
            fontSize: 14
          }}
          onSubmit={e => e.preventDefault()}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <InputLabel style={{ color: RADOM_COLORS.GRAY_DARKEST, opacity: 1 }}>Email address</InputLabel>
            <TextInput
              placeholder='Enter email address'
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoFocus />
          </div>

          <LoginButton
            isLoading={isLoading}
            style={{
              backgroundColor: RADOM_COLORS.ORANGE
            }}
            onClick={async () => {
              setErr('')
              setIsLoading(true)
              try {
                const recaptchaToken = await getRecaptchaToken('email_start_signup')
                await Radom.startEmailSignUp(email, recaptchaToken)
                props.setStep(LoginStep.VerifySignUp)
              } catch (err) {
                setErr(getErrorMessage(err))
              } finally {
                setIsLoading(false)
              }
            }}>
            <span>Continue</span>
          </LoginButton>
        </form>
      }

      {
        step === LoginStep.PasswordResetCodeVerified &&
        <form
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 15,
            width: '100%',
            fontSize: 14
          }}
          onSubmit={e => e.preventDefault()}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <InputLabel style={{ color: RADOM_COLORS.GRAY_DARKEST, opacity: 1 }}>Password</InputLabel>
            <TextInput
              type='password'
              placeholder='Enter password'
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoFocus />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <InputLabel style={{ color: RADOM_COLORS.GRAY_DARKEST, opacity: 1 }}>Confirm password</InputLabel>
            <TextInput
              type='password'
              placeholder='Confirm password'
              value={passwordConfirm}
              onChange={e => setPasswordConfirm(e.target.value)} />
          </div>

          <LoginButton
            isLoading={isLoading}
            style={{
              backgroundColor: RADOM_COLORS.ORANGE
            }}
            onClick={() => {
              setIsLoading(true)
              setErr('')
              Radom.completePasswordReset(email, password, code)
                .then(res => {
                  Radom.token = res.jwtToken
                  try {
                    localStorage.setItem(RADOM_TOKEN_LOCAL_STORAGE_KEY, Radom.token)
                    localStorage.setItem(RADOM_TOKEN_EXPIRY_LOCAL_STORAGE_KEY, new Date(res.expiresAt).toString())
                  } catch (err) {
                    localStorage.clear()
                    window.location = '/' as any
                  }
                  setSearchParams({})
                  Radom.isLoggedIn = !res.requires2fa
                  Radom.requires2fa = res.requires2fa
                  if (!res.requires2fa) {
                    successToast('Successfully signed in!')
                  }
                })
                .catch(err => {
                  setErr(getErrorMessage(err))
                })
                .finally(() => {
                  setIsLoading(false)
                })
            }}>
            <span>Set password</span>
          </LoginButton>
        </form>
      }

      {
        step === LoginStep.ForgotPassword &&
        <form
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 15,
            width: '100%',
            fontSize: 14
          }}
          onSubmit={e => e.preventDefault()}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <InputLabel style={{ color: RADOM_COLORS.GRAY_DARKEST, opacity: 1 }}>Email address</InputLabel>
            <TextInput
              placeholder='Enter email address'
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoFocus />
          </div>

          <LoginButton
            isLoading={isLoading}
            style={{
              backgroundColor: RADOM_COLORS.ORANGE
            }}
            onClick={async () => {
              setErr('')
              setIsLoading(true)
              try {
                const recaptchaToken = await getRecaptchaToken('email_start_password_reset')
                await Radom.startPasswordReset(email, recaptchaToken)
                props.setStep(LoginStep.PasswordResetEmailSent)
              } catch (err) {
                setErr(getErrorMessage(err))
              } finally {
                setIsLoading(false)
              }
            }}>
            <span>Reset password</span>
          </LoginButton>
        </form>
      }

      {
        step === LoginStep.CompleteSignUp &&
        <form
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 15,
            width: '100%',
            fontSize: 14
          }}
          onSubmit={e => {
            e.preventDefault()
            completeSignUp()
          }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <InputLabel style={{ color: RADOM_COLORS.GRAY_DARKEST, opacity: 1 }}>Email address</InputLabel>
            <TextInput
              placeholder='Enter email address'
              value={email}
              disabled />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <InputLabel style={{ color: RADOM_COLORS.GRAY_DARKEST, opacity: 1 }}>Password</InputLabel>
            <TextInput
              type='password'
              placeholder='Enter password'
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoFocus />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <InputLabel style={{ color: RADOM_COLORS.GRAY_DARKEST, opacity: 1 }}>Confirm password</InputLabel>
            <TextInput
              type='password'
              placeholder='Confirm password'
              value={passwordConfirm}
              onChange={e => setPasswordConfirm(e.target.value)} />
          </div>

          <LoginButton
            isLoading={isLoading}
            style={{
              backgroundColor: RADOM_COLORS.ORANGE
            }}
            onClick={async () => await completeSignUp()}>
            Continue
          </LoginButton>
        </form>
      }

    </div>
})

const EmailLogin = (props: IProps): JSX.Element => {
  return <GoogleReCaptchaProvider
    reCaptchaKey={GOOGLE_RECAPTCHA_SITE_KEY}
    scriptProps={{ async: true, defer: true, appendTo: 'head' }}>
    <EmailLoginInner {...props} />
  </GoogleReCaptchaProvider>
}

export default EmailLogin
